import React, { useEffect, useRef, useState } from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useTranslation } from 'next-i18next';
import { DTOQualityUserBadge } from 'src/apis/dto/DTOQualityUserBadge';
import { hexToRgb } from 'src/utils/common';
const Root = styled.div`
  padding: 57px 16px 38px;
  border-radius: 16px;
  background: ${({
  theme
}) => theme.color.surface1};
`;
const ProgressBar = styled.div`
  height: 3px;
  background-color: ${({
  theme
}) => `rgba(${hexToRgb(theme.color.surface12)},0.2)`};
  position: relative;
  border-radius: 5px;
`;
const fillAnimation = (percentage: number) => keyframes`
  from {
    width: 0%;
  } to {
    width: ${percentage}%;
  }
`;
const ProgressBarInner = styled.div<{
  percentage: number;
}>`
  position: absolute;
  background-color: ${({
  theme
}) => theme.color.surface12};
  height: 3px;
  ${({
  theme
}) => theme.isRtl ? 'right' : 'left'}: 0px;
  border-radius: 5px;
  animation-name: ${({
  percentage
}) => fillAnimation(percentage)};
  animation-fill-mode: forwards;
  animation-duration: 0.8s;
`;
const fadeIn = keyframes`
  from {
    opacity: 0;
  } to {
    opacity: 1;
  }
`;
const ProgressCurrentAmount = styled.div<{
  percentage: number;
  amountPosition?: 'start' | 'end';
}>`
  position: absolute;
  bottom: 14px;
  background-color: ${({
  theme
}) => theme.color.surface12};
  border-radius: 8px;
  padding: 6px 8px;
  display: flex;
  gap: 4px;
  align-items: center;
  font-weight: 700;
  font-size: 12px;
  color: ${({
  theme
}) => theme.color.contentInverse};
  ${({
  amountPosition,
  percentage,
  theme
}) => {
  if (!amountPosition) {
    return `
        ${theme.isRtl ? 'right' : 'left'}: ${percentage}%;
        transform: translateX(${theme.isRtl ? '' : '-'}50%);
      `;
  } else if (amountPosition === 'start') {
    return `
        left: 0px;
      `;
  } else if (amountPosition === 'end') {
    return `
        right: 0px;
      `;
  }
}}
  animation-name: ${fadeIn};
  animation-fill-mode: forwards;
  opacity: 0;
  animation-duration: 0.6s;
  animation-delay: 0.8s;
`;
const ProgressIcon = styled.img`
  width: 16px;
  height: 16px;
`;
const ProgressDotRating = styled.div`
  font-weight: 700;
  font-size: 12px;
  line-height: 14px;
  color: ${({
  theme
}) => theme.color.content11};
  margin-bottom: 2px;
`;
const ProgressDotWrap = styled.div<{
  percentage: number;
}>`
  display: flex;
  position: absolute;
  top: 0px;
  flex-direction: column;
  &:before {
    display: flex;
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 5px;
    margin-top: -1px;
    margin-bottom: 7px;
  }
  ${({
  theme,
  percentage
}) => {
  if (percentage === 0) {
    return `
        ${theme.isRtl ? 'right' : 'left'}: 0px;
        align-items: flex-start;
      `;
  }
  if (percentage === 100) {
    return `
        ${!theme.isRtl ? 'right' : 'left'}: 0px;
        align-items: flex-end;
      `;
  }
  return `
      &:before {
        background-color: ${theme.color.surface12};
      }
      ${theme.isRtl ? 'right' : 'left'}: ${percentage}%;
      transform: translateX(${theme.isRtl ? '' : '-'}50%);
      align-items: center;
    `;
}}
`;
const dotAnimation = keyframes`
  from {
    width: 0px;
    height: 0px;
  } 50% {
    width: 15px;
    height: 15px;
  } to {
    width: 0px;
    height: 0px;
  }
`;
const ProgressCurrentDot = styled.div`
  background-color: ${({
  theme
}) => theme.color.surface12};
  display: flex;
  width: 7px;
  height: 7px;
  border-radius: 7px;
  transform: translate(${({
  theme
}) => `${theme.isRtl ? '-' : ''}50%`}, -50%);
  margin-top: 1.5px;
  ${({
  theme
}) => !theme.isRtl ? 'right' : 'left'}: 0px;
  position: absolute;
`;
const ProgressCurrentDotBack = styled(ProgressCurrentDot)`
  width: 0px;
  height: 0px;
  border-radius: 15px;
  background-color: ${({
  theme
}) => theme.color.surface12};
  opacity: 0.2;
  animation-name: ${dotAnimation};
  animation-iteration-count: infinite;
  animation-duration: 3s;
`;
const ProgressAmount = styled.div`
  white-space: nowrap;
`;
interface Props {
  qualityUserBadge: DTOQualityUserBadge;
}
const Progress: React.FC<Props> = ({
  qualityUserBadge: {
    percentage,
    state,
    badgeImageUrl
  }
}) => {
  const {
    t
  } = useTranslation();
  const amountRef = useRef<HTMLDivElement | null>(null);
  const [amountPosition, setAmountPosition] = useState<undefined | 'start' | 'end'>(undefined);
  useEffect(() => {
    const dom = amountRef.current;
    const parent = dom?.parentElement;
    if (dom && parent && percentage !== undefined) {
      const handler = () => {
        if (dom.offsetLeft < parent.offsetLeft) {
          setAmountPosition('start');
        } else if (dom.offsetLeft >= parent.offsetWidth - dom.offsetWidth) {
          setAmountPosition('end');
        } else {
          setAmountPosition(undefined);
        }
      };
      window.addEventListener('resize', handler);
      handler();
      return () => {
        window.removeEventListener('resize', handler);
      };
    }
  }, [percentage]);
  return <Root data-sentry-element="Root" data-sentry-component="Progress" data-sentry-source-file="QualityUserCard.tsx">
      <ProgressBar data-sentry-element="ProgressBar" data-sentry-source-file="QualityUserCard.tsx">
        <ProgressDotWrap percentage={100} data-sentry-element="ProgressDotWrap" data-sentry-source-file="QualityUserCard.tsx">
          <ProgressDotRating data-sentry-element="ProgressDotRating" data-sentry-source-file="QualityUserCard.tsx">{t('badge_progress_goal')}</ProgressDotRating>
        </ProgressDotWrap>
        <ProgressBarInner percentage={percentage} data-sentry-element="ProgressBarInner" data-sentry-source-file="QualityUserCard.tsx">
          <ProgressCurrentDot data-sentry-element="ProgressCurrentDot" data-sentry-source-file="QualityUserCard.tsx" />
          <ProgressCurrentDotBack data-sentry-element="ProgressCurrentDotBack" data-sentry-source-file="QualityUserCard.tsx" />
        </ProgressBarInner>
        <ProgressCurrentAmount percentage={percentage} ref={amountRef} amountPosition={amountPosition} data-sentry-element="ProgressCurrentAmount" data-sentry-source-file="QualityUserCard.tsx">
          <ProgressIcon src={badgeImageUrl} alt='Quality User Badge' data-sentry-element="ProgressIcon" data-sentry-source-file="QualityUserCard.tsx" />
          <ProgressAmount data-sentry-element="ProgressAmount" data-sentry-source-file="QualityUserCard.tsx">
            {state === 'ACHIEVED' ? t('badge_progress_description_complete') : `${percentage}%`}
          </ProgressAmount>
        </ProgressCurrentAmount>
      </ProgressBar>
    </Root>;
};
export default React.memo(Progress);